import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
function formatCountdown(expiresAt) {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return 'Abgelaufen';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatDateTime(dt) {
  if (!dt) return '–';
  return new Date(dt).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const FreeAgencyPage = () => {
  const { seasonId } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [fa, setFa] = useState(null);
  const [picks, setPicks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [publicPlayers, setPublicPlayers] = useState([]);
  const [publicSearch, setPublicSearch] = useState('');
  const [publicPosition, setPublicPosition] = useState('');
  const [playerSearch, setPlayerSearch] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [picking, setPicking] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Live ticker
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-refresh
  useEffect(() => {
    const t = setInterval(fetchState, 15000);
    return () => clearInterval(t);
  }, [seasonId]);

  useEffect(() => {
    fetchState();
  }, [seasonId]);

  useEffect(() => {
    // Auf die betrachtete Saison scopen, damit die Liste die Free Agents dieser
    // Saison zeigt – unabhängig davon, welche Saison global als aktiv markiert ist.
    api.get(`/players/free-agents?seasonId=${seasonId}`)
      .then(res => setPublicPlayers(res.data))
      .catch(() => {});
  }, [seasonId, picks]); // refresh whenever pick board changes

  const fetchState = async () => {
    try {
      const res = await api.get(`/free-agency/season/${seasonId}`);
      setFa(res.data.freeAgency);
      setPicks(res.data.picks);
      setTeams(res.data.teams);
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  const fetchAvailablePlayers = useCallback(async () => {
    if (!fa || fa.status !== 'in_progress') return;
    try {
      const params = new URLSearchParams();
      if (playerSearch) params.set('search', playerSearch);
      if (positionFilter) params.set('position', positionFilter);
      const res = await api.get(`/free-agency/${fa.id}/available-players?${params}`);
      setAvailablePlayers(res.data.players);
    } catch (e) {
      console.error(e);
    }
  }, [fa, playerSearch, positionFilter]);

  useEffect(() => {
    fetchAvailablePlayers();
  }, [fetchAvailablePlayers]);

  // Determine if the logged-in user is a GM with an active/expired but unpicked slot
  const myTeamId = user?.teamId;
  const myActivePick = picks.find(
    p => p.teamId === myTeamId && (p.status === 'active' || p.status === 'expired') && !p.playerId
  );
  const isMyTurn = !!myActivePick;
  const isAdmin = user?.role === 'admin';

  const activePick = picks.find(p => p.status === 'active');
  const expiredUnpicked = picks.filter(p => p.status === 'expired' && !p.playerId);

  const handleMakePick = async () => {
    if (!selectedPlayerId || !myActivePick) return;
    setErrorMsg('');
    setSuccessMsg('');
    setPicking(true);
    try {
      const res = await api.post(`/free-agency/${fa.id}/picks/${myActivePick.id}`, {
        playerId: parseInt(selectedPlayerId)
      });
      setSuccessMsg(`Pick erfolgreich: ${res.data.player?.firstName} ${res.data.player?.lastName}`);
      setSelectedPlayerId('');
      await fetchState();
      await fetchAvailablePlayers();
    } catch (e) {
      setErrorMsg(e.response?.data?.message || 'Fehler beim Pick');
    } finally {
      setPicking(false);
    }
  };

  const handleSkip = async () => {
    if (!myActivePick) return;
    if (!window.confirm('Pick überspringen? Das nächste Team kommt an die Reihe. Du kannst deinen Pick später noch nachholen.')) return;
    setErrorMsg('');
    setSuccessMsg('');
    setPicking(true);
    try {
      await api.post(`/free-agency/${fa.id}/picks/${myActivePick.id}/skip`);
      setSuccessMsg('Pick übersprungen – du kannst ihn später noch nachholen.');
      setSelectedPlayerId('');
      await fetchState();
      await fetchAvailablePlayers();
    } catch (e) {
      setErrorMsg(e.response?.data?.message || 'Fehler beim Überspringen');
    } finally {
      setPicking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Lade Free Agency…</div>
      </div>
    );
  }

  if (!fa) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4">🏀</div>
          <h1 className="text-2xl font-bold text-white mb-2">Keine Free Agency</h1>
          <p className="text-gray-400">Für diese Season wurde noch keine Free Agency eingerichtet.</p>
        </div>
      </div>
    );
  }

  const completedCount = picks.filter(p => p.status === 'completed').length;
  const totalPicks = picks.length;

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Hero header */}
      <div className="bg-gradient-to-r from-blue-900 to-yellow-700 px-6 py-10">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl font-bold mb-1">{fa.name}</h1>
          <p className="text-blue-200 text-sm">Täglich {fa.dailyStartTime} – {fa.dailyEndTime} · {fa.pickTimeMinutes} Min/Pick</p>
          <div className="flex items-center gap-4 mt-4">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
              fa.status === 'not_started' ? 'bg-gray-600 text-white' :
              fa.status === 'in_progress' ? 'bg-green-500 text-white' : 'bg-blue-50 dark:bg-blue-900/200 text-white'
            }`}>
              {fa.status === 'not_started' ? '⏳ Noch nicht gestartet' :
               fa.status === 'in_progress' ? '🟢 Läuft' : '✅ Abgeschlossen'}
            </span>
            <span className="text-sm text-blue-200">{completedCount}/{totalPicks} Picks gemacht</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-8">

        {/* ── Status: not started ── */}
        {fa.status === 'not_started' && (
          <div className="bg-gray-800 rounded-xl p-6 text-center">
            <div className="text-5xl mb-3">⏳</div>
            <h2 className="text-xl font-bold mb-2">Free Agency startet bald</h2>
            <p className="text-gray-400">Geplanter Start: {formatDateTime(fa.startDate)}</p>
          </div>
        )}

        {/* ── Active: my turn notification ── */}
        {fa.status === 'in_progress' && isMyTurn && (
          <div className="bg-green-900 border-2 border-green-500 rounded-xl p-6">
            <h2 className="text-xl font-bold text-green-300 mb-1">🏀 Du bist dran!</h2>
            <p className="text-gray-300 text-sm mb-4">
              R{myActivePick.round} · Pick {myActivePick.pickNumber}
              {myActivePick.windowExpires && (
                <span className={`ml-3 font-mono font-bold ${
                  new Date(myActivePick.windowExpires) - new Date() < 600000 ? 'text-red-400' : 'text-yellow-300'
                }`}>
                  ⏱ {formatCountdown(myActivePick.windowExpires)}
                </span>
              )}
            </p>

            {successMsg && (
              <div className="bg-green-800 text-green-200 px-4 py-2 rounded-lg mb-4 text-sm">{successMsg}</div>
            )}
            {errorMsg && (
              <div className="bg-red-900 text-red-300 px-4 py-2 rounded-lg mb-4 text-sm">{errorMsg}</div>
            )}

            {/* Player search & pick */}
            <div className="flex gap-3 mb-4">
              <input
                type="text"
                placeholder="Spieler suchen…"
                className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white"
                value={playerSearch}
                onChange={e => setPlayerSearch(e.target.value)}
              />
              <select
                className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white"
                value={positionFilter}
                onChange={e => setPositionFilter(e.target.value)}
              >
                <option value="">Alle Pos.</option>
                {['PG', 'SG', 'SF', 'PF', 'C'].map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            </div>

            <div className="overflow-y-auto max-h-64 mb-4">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-green-900">
                  <tr className="text-gray-400 border-b border-green-700">
                    <th className="text-left pb-2 pr-3">Spieler</th>
                    <th className="text-left pb-2 pr-3">Pos</th>
                    <th className="text-left pb-2">OVR</th>
                  </tr>
                </thead>
                <tbody>
                  {availablePlayers.map(p => (
                    <tr
                      key={p.id}
                      className={`border-b border-green-800 cursor-pointer hover:bg-green-800 ${selectedPlayerId === String(p.id) ? 'bg-blue-900' : ''}`}
                      onClick={() => setSelectedPlayerId(String(p.id))}
                    >
                      <td className="py-2 pr-3 font-medium">{p.firstName} {p.lastName}</td>
                      <td className="py-2 pr-3 text-gray-400">{p.position}</td>
                      <td className="py-2">
                        <span className="bg-blue-900 text-blue-200 text-xs px-2 py-0.5 rounded-full">{p.overallRating}</span>
                      </td>
                    </tr>
                  ))}
                  {availablePlayers.length === 0 && (
                    <tr><td colSpan={3} className="py-4 text-center text-gray-500 dark:text-gray-400">Keine Spieler gefunden</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <button
              onClick={handleMakePick}
              disabled={picking || !selectedPlayerId}
              className="bg-green-600 hover:bg-green-500 disabled:opacity-50 px-6 py-3 rounded-xl font-bold text-lg w-full"
            >
              {picking ? 'Pick wird gemacht…' : selectedPlayerId ? `Spieler #${selectedPlayerId} picken ✓` : 'Spieler auswählen'}
            </button>

            {myActivePick.status === 'active' && (
              <button
                onClick={handleSkip}
                disabled={picking}
                className="mt-3 w-full bg-gray-700 hover:bg-gray-600 disabled:opacity-50 px-6 py-2 rounded-xl text-sm font-semibold text-gray-200"
              >
                Pick überspringen (später nachholbar)
              </button>
            )}
          </div>
        )}

        {/* ── Active: current picker (for others to watch) ── */}
        {fa.status === 'in_progress' && activePick && !isMyTurn && (
          <div className="bg-gray-800 border border-gray-600 rounded-xl p-5">
            <h3 className="text-sm uppercase text-gray-400 font-semibold mb-2">Jetzt am Zug</h3>
            <div className="flex items-center gap-4">
              <div className="text-3xl">🏀</div>
              <div>
                <div className="text-xl font-bold">{activePick.team?.name}</div>
                <div className="text-sm text-gray-400">R{activePick.round} · Pick {activePick.pickNumber}</div>
              </div>
              {activePick.windowExpires && (
                <div className="ml-auto text-right">
                  <div className={`text-2xl font-mono font-bold ${
                    new Date(activePick.windowExpires) - new Date() < 600000 ? 'text-red-400' : 'text-yellow-300'
                  }`}>
                    {formatCountdown(activePick.windowExpires)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Läuft ab: {formatDateTime(activePick.windowExpires)}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Expired / pending picks notice ── */}
        {fa.status === 'in_progress' && expiredUnpicked.length > 0 && (
          <div className="bg-orange-900 border border-orange-600 rounded-xl p-5">
            <h3 className="text-sm uppercase text-orange-300 font-semibold mb-2">Ausstehende Picks</h3>
            <p className="text-sm text-gray-300 mb-3">Diese GMs sind noch offen (Zeit abgelaufen oder übersprungen) und können weiterhin picken:</p>
            <div className="flex flex-wrap gap-2">
              {expiredUnpicked.map(p => (
                <div key={p.id} className={`rounded-lg px-3 py-2 text-sm ${p.wasSkipped ? 'bg-indigo-800' : 'bg-orange-800'}`}>
                  <span className="font-semibold">{p.team?.name}</span>
                  <span className={`text-xs ml-2 ${p.wasSkipped ? 'text-indigo-200' : 'text-orange-300'}`}>R{p.round}·{p.pickNumber}</span>
                  <span className={`text-xs ml-2 ${p.wasSkipped ? 'text-indigo-300' : 'text-orange-300'}`}>{p.wasSkipped ? '⏭️ übersprungen' : '🟠 abgelaufen'}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Next up ── */}
        {fa.status === 'in_progress' && (
          <div className="bg-gray-800 rounded-xl p-5">
            <h3 className="text-lg font-bold mb-4">Nächste Picks</h3>
            <div className="space-y-1">
              {picks.filter(p => p.status === 'waiting').slice(0, 10).map((p, i) => (
                <div key={p.id} className="flex items-center gap-4 py-1 border-b border-gray-700">
                  <span className="text-gray-500 dark:text-gray-400 text-xs w-6">{i + 1}.</span>
                  <span className="text-sm font-medium w-36">{p.team?.name}</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Runde {p.round}</span>
                  {p.teamId === myTeamId && (
                    <span className="text-xs bg-blue-800 text-blue-200 px-2 py-0.5 rounded-full">Du</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Pick board (all rounds) ── */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-xl font-bold mb-6">Pick-Board</h2>
          {Array.from({ length: fa.totalRounds }, (_, i) => i + 1).map(round => (
            <div key={round} className="mb-8">
              <h3 className="text-base font-semibold text-yellow-300 mb-3 flex items-center gap-2">
                <span>Runde {round}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">
                  {picks.filter(p => p.round === round && p.status === 'completed').length}/
                  {picks.filter(p => p.round === round).length} Picks
                </span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {picks.filter(p => p.round === round).map(p => (
                  <div
                    key={p.id}
                    className={`flex items-center gap-3 rounded-lg px-4 py-3 ${
                      p.status === 'completed' ? 'bg-gray-700' :
                      p.status === 'active' ? 'bg-green-900 border border-green-600' :
                      p.status === 'expired' && !p.playerId ? (p.wasSkipped ? 'bg-indigo-900 border border-indigo-700' : 'bg-orange-900 border border-orange-700') :
                      'bg-gray-750 opacity-60'
                    } ${p.teamId === myTeamId ? 'ring-1 ring-blue-500' : ''}`}
                  >
                    <span className="text-gray-500 dark:text-gray-400 text-xs w-6">#{p.overallPick}</span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{p.team?.name || `Team ${p.teamId}`}</div>
                      {p.player ? (
                        <div className="text-xs text-gray-300 mt-0.5">
                          {p.player.firstName} {p.player.lastName}
                          <span className="text-gray-500 dark:text-gray-400 ml-1">{p.player.position} · OVR {p.player.overallRating}</span>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {p.status === 'active' ? '🟢 Am Zug…' :
                           p.status === 'expired' ? (p.wasSkipped ? '⏭️ Übersprungen' : '🟠 Zeit abgelaufen') : '⏳ Wartet'}
                        </div>
                      )}
                    </div>
                    {p.pickedAt && (
                      <span className="text-xs text-gray-600 dark:text-gray-400">{formatDateTime(p.pickedAt)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ── Available Players (public) ── */}
        <div className="bg-gray-800 rounded-xl p-6">
          <h2 className="text-xl font-bold mb-1">Verfügbare Spieler</h2>
          <p className="text-gray-400 text-sm mb-4">Alle nicht gerostereten Spieler · sortiert nach OVR</p>

          {/* Filters */}
          <div className="flex gap-3 mb-4">
            <input
              type="text"
              placeholder="Spieler suchen…"
              className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white"
              value={publicSearch}
              onChange={e => setPublicSearch(e.target.value)}
            />
            <select
              className="bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white"
              value={publicPosition}
              onChange={e => setPublicPosition(e.target.value)}
            >
              <option value="">Alle Pos.</option>
              {['PG', 'SG', 'SF', 'PF', 'C'].map(pos => (
                <option key={pos} value={pos}>{pos}</option>
              ))}
            </select>
          </div>

          {(() => {
            const filtered = publicPlayers.filter(p => {
              const name = `${p.firstName} ${p.lastName}`.toLowerCase();
              if (publicSearch && !name.includes(publicSearch.toLowerCase())) return false;
              if (publicPosition && !p.position.includes(publicPosition)) return false;
              return true;
            });
            return (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{filtered.length} Spieler gefunden</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-400 border-b border-gray-700 text-left">
                        <th className="pb-2 pr-3 w-8">#</th>
                        <th className="pb-2 pr-3">Spieler</th>
                        <th className="pb-2 pr-3">Pos</th>
                        <th className="pb-2 pr-3">OVR</th>
                        <th className="pb-2">Badges</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p, i) => (
                        <tr key={p.id} className="border-b border-gray-700 hover:bg-gray-700 transition-colors">
                          <td className="py-2 pr-3 text-gray-500 dark:text-gray-400">{i + 1}</td>
                          <td className="py-2 pr-3 font-medium">{p.firstName} {p.lastName}</td>
                          <td className="py-2 pr-3 text-gray-400">{p.position}</td>
                          <td className="py-2 pr-3">
                            <span className="bg-blue-900 text-blue-200 text-xs font-bold px-2 py-0.5 rounded-full">{p.overallRating}</span>
                          </td>
                          <td className="py-2 text-yellow-400 font-semibold">{p.badges || 0}</td>
                        </tr>
                      ))}
                      {filtered.length === 0 && (
                        <tr><td colSpan={5} className="py-6 text-center text-gray-500 dark:text-gray-400">Keine Spieler gefunden</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}
        </div>

      </div>
    </div>
  );
};

export default FreeAgencyPage;
